import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Typography } from "@mui/material";
import { Drawer, TextField, toast } from "@outpave/ds";
import { toastMutationError } from "utils/toastMutationError";
import { useUnsavedChangesGuard } from "hooks/useUnsavedChangesGuard";
import PlatformIntegrationFieldType from "enums/PlatformIntegrationFieldType";
import {
  integrationValue,
  PlatformIntegration,
  PlatformIntegrationCheck,
  PlatformIntegrationField,
  PlatformIntegrationTemplate,
  PlatformIntegrationTemplateSection,
  SavePlatformIntegrationInput,
} from "hooks/usePlatformIntegrations";
import { CATEGORY_LABEL_KEY } from "utils/platformIntegrations/integrationLabels";
import { Stack } from "components/layout/reusableLayouts";
import SectionTabs from "components/common/SectionTabs";
import IntegrationFieldInput from "./IntegrationFieldInput";

const CREDENTIALS_SECTION = "CREDENTIALS";

interface IntegrationFormDrawerProps {
  open: boolean;
  template: PlatformIntegrationTemplate | null;
  /** Absent when connecting a provider rather than changing an integration. */
  integration?: PlatformIntegration | null;
  /** The one section being changed. Connecting fills every section. */
  sectionKey?: string | null;
  defaultName?: string;
  onClose: () => void;
  onSave: (
    input: SavePlatformIntegrationInput,
  ) => Promise<PlatformIntegration | undefined>;
  onCheck: (
    input: SavePlatformIntegrationInput,
  ) => Promise<PlatformIntegrationCheck>;
  onSaved?: (integration: PlatformIntegration) => void;
}

/**
 * Connects a provider, or changes one section of an integration, from the
 * provider's template. Secrets are write only: a blank secret leaves the
 * stored one in place.
 */
const IntegrationFormDrawer: React.FC<IntegrationFormDrawerProps> = ({
  open,
  template,
  integration,
  sectionKey,
  defaultName = "",
  onClose,
  onSave,
  onCheck,
  onSaved,
}) => {
  const { t } = useTranslation();
  const connecting = !integration;
  const [name, setName] = useState("");
  const [values, setValues] = useState<Record<string, string>>({});
  const [check, setCheck] = useState<PlatformIntegrationCheck | null>(null);
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showMissing, setShowMissing] = useState(false);
  const [focusKey, setFocusKey] = useState<string | null>(null);

  const sections = useMemo(
    () =>
      // Connecting asks only for what a connection needs: a section with a
      // required field or a webhook to register. Optional ones, and any that
      // depend on card products a new integration has yet to learn, wait for
      // the detail page.
      (template?.sections ?? []).filter((section) =>
        connecting
          ? !section.forSubAccountTypes.length &&
            section.fields.some(
              (field) => field.required || Boolean(field.endpointUrl),
            )
          : section.key === sectionKey,
      ),
    [template, connecting, sectionKey],
  );
  const fields = useMemo(
    () => sections.flatMap((section) => section.fields),
    [sections],
  );
  const initialValues = useMemo(
    () =>
      Object.fromEntries(
        fields.map((field) => [
          field.key,
          field.type === PlatformIntegrationFieldType.SECRET
            ? ""
            : integrationValue(integration, field.key) ||
              (field.defaultValue ?? ""),
        ]),
      ),
    [fields, integration],
  );

  const initialName = integration?.name ?? defaultName;

  useEffect(() => {
    if (!open) return;
    setName(initialName);
    setValues(initialValues);
    setCheck(null);
    setShowMissing(false);
  }, [open, initialName, initialValues]);

  // The tabs act on a change of focusKey, so clearing it lets the same tab be brought forward again.
  useEffect(() => {
    if (focusKey) setFocusKey(null);
  }, [focusKey]);

  const stored = (key: string) =>
    Boolean(integration?.storedSecrets.includes(key));

  // Connecting sends what was typed; a change sends only what moved, so a
  // blank secret is never read as clearing it.
  const changedValues = () =>
    fields
      .filter((field) => {
        const value = (values[field.key] ?? "").trim();
        if (field.type === PlatformIntegrationFieldType.SECRET) return !!value;
        return connecting ? !!value : value !== initialValues[field.key];
      })
      .map((field) => ({
        key: field.key,
        value: (values[field.key] ?? "").trim(),
      }));

  const isMissing = (field: PlatformIntegrationField) =>
    field.required && !stored(field.key) && !(values[field.key] ?? "").trim();
  const canCheck = sections.some(
    (section) => section.key === CREDENTIALS_SECTION,
  );

  const setValue = (key: string, value: string) => {
    setValues((current) => ({ ...current, [key]: value }));
    setCheck(null);
  };

  const runCheck = async () => {
    if (!template) return;
    setChecking(true);
    try {
      setCheck(
        await onCheck({
          id: integration?.id,
          category: template.category,
          provider: template.provider,
          values: changedValues(),
        }),
      );
    } catch (error: unknown) {
      setCheck({
        ok: false,
        message: errorMessage(error, t("Please try again")),
      });
    } finally {
      setChecking(false);
    }
  };

  const handleSubmit = async () => {
    if (!template) return;
    const incomplete = sections.find((section) =>
      section.fields.some(isMissing),
    );
    if (connecting && incomplete) {
      setShowMissing(true);
      setFocusKey(incomplete.key);
      return;
    }
    setSubmitting(true);
    try {
      const saved = await onSave({
        ...(connecting
          ? {
              category: template.category,
              provider: template.provider,
              name: name.trim(),
            }
          : { id: integration?.id }),
        values: changedValues(),
      });
      toast.success(
        connecting
          ? t("IntegrationConnectedToast", {
              provider: template.label,
            })
          : t("IntegrationSavedToast"),
      );
      if (saved) onSaved?.(saved);
      onClose();
    } catch (error: unknown) {
      toastMutationError(error, t("IntegrationSaveError"));
    } finally {
      setSubmitting(false);
    }
  };

  const renderSection = (section: PlatformIntegrationTemplateSection) => (
    <Stack gap="var(--space-4)">
      {section.descriptionKey && (
        <Typography
          variant="body2"
          sx={{ color: "var(--color-text-secondary)" }}
        >
          {t(section.descriptionKey)}
        </Typography>
      )}
      {section.fields.map((field) => (
        <IntegrationFieldInput
          key={field.key}
          field={field}
          value={values[field.key] ?? ""}
          stored={stored(field.key)}
          onChange={(value) => setValue(field.key, value)}
          disabled={submitting}
          error={showMissing && isMissing(field) ? t("Required") : undefined}
        />
      ))}
    </Stack>
  );

  const changed =
    name !== initialName ||
    fields.some(
      (field) => (values[field.key] ?? "") !== initialValues[field.key],
    );
  const { requestLeave, dialog } = useUnsavedChangesGuard(changed, onClose);

  const handleClose = () => {
    if (submitting) return;
    requestLeave();
  };

  const title = connecting
    ? t("ConnectProviderFor", {
        provider: template?.label,
        category: template ? t(CATEGORY_LABEL_KEY[template.category]) : "",
      })
    : t(sections[0]?.labelKey ?? "Edit");

  return (
    <>
      <Drawer
        open={open}
        onClose={handleClose}
        title={title}
        loading={submitting}
        footer={
          <>
            {canCheck && (
              <Button
                variant="default-outlined"
                onClick={() => void runCheck()}
                disabled={checking || submitting}
                sx={{ marginInlineEnd: "auto" }}
              >
                {t("Test connection")}
              </Button>
            )}
            <Button
              variant="primary-outlined"
              onClick={handleClose}
              disabled={submitting}
            >
              {t("Cancel")}
            </Button>
            <Button
              variant="primary-filled"
              onClick={() => void handleSubmit()}
              disabled={submitting || (connecting && !name.trim())}
            >
              {connecting ? t("Connect") : t("Save")}
            </Button>
          </>
        }
      >
        <Stack gap="var(--space-6)">
          {connecting && template && (
            <Typography
              variant="body2"
              sx={{ color: "var(--color-text-secondary)" }}
            >
              {t(template.descriptionKey)}
            </Typography>
          )}

          {check && (
            <Alert
              severity={check.ok ? "success" : "error"}
              role={check.ok ? "status" : "alert"}
            >
              {check.ok
                ? t("ConnectionCheckPassed", {
                    provider: template?.label,
                  })
                : check.message || t("ConnectionCheckFailed")}
            </Alert>
          )}

          {connecting && (
            <TextField
              label={t("Name")}
              value={name}
              onChange={(event) => setName(event.target.value)}
              helperText={t("IntegrationNameHelp")}
              required
              fullWidth
            />
          )}

          {connecting ? (
            <SectionTabs
              ariaLabel={t("Settings")}
              focusKey={focusKey}
              tabs={sections.map((section) => ({
                key: section.key,
                label: t(section.labelKey),
                panel: renderSection(section),
              }))}
            />
          ) : (
            sections.map((section) => (
              <React.Fragment key={section.key}>
                {renderSection(section)}
              </React.Fragment>
            ))
          )}
        </Stack>
      </Drawer>
      {dialog}
    </>
  );
};

const errorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

export default IntegrationFormDrawer;
